import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  ContentTemplate,
  Divider,
  Grid,
  KeyValue,
  Progress,
  SectionHeader,
  TitleHeader,
  Typography,
} from 'web-library';
import { useHistory } from 'react-router-dom';
import RouteKey from '../../../routes/key';
import {
  useGetNonNationalWithdrawalInstruction
} from '../../../api-refactored/non-national-exam-registration/useGetNonNationalExamWithdrawalInstruction';
import {
  usePostCreateNonNationalExamWithdrawalDraft,
  WithdrawalDraftResponse,
} from '../../../api-refactored/non-national-exam-registration/usePostCreateNonNationalExamWithdrawalDraft';
import { useWOGAA } from '../../../hooks/wogaa';

const useStyles = makeStyles()((theme) =>
({
  headerStatement: {
    marginBottom: theme.spacing(4),
  },
  paragraph: {
    marginBottom: theme.spacing(1),
  },
  button: {
    marginBottom: theme.spacing(7),
    height: 'fit-content',
  },
  errorMessage: {
    marginBottom: theme.spacing(2),
  },
}));

const { withdrawal } = RouteKey.child;
const { examRegistrations, nonNationalReason } = RouteKey.withdrawal.child;

const GridKeyValue = ({ label, value }: { label: string; value: string | null }) => (
  <Grid xl={12} lg={12} md={12} sm={12} xs={12}>
    <KeyValue keyText={label} valueText={value || '-'} capitalize={false} />
  </Grid>
);

const WithdrawFromExam = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState<string>();

  // Wogaa tracking start for non national withdrawal //

  const { transactionStart } = useWOGAA('myexamsseab-6458');

  const {
    data: withdrawalInstructionData,
    isLoading: isLoadingWithdrawalInstructionData,
  } = useGetNonNationalWithdrawalInstruction();

  const {
    mutate: mutatePostCreateExamWithdrawalDraft,
  } = usePostCreateNonNationalExamWithdrawalDraft({
    onSuccess: (data: WithdrawalDraftResponse) =>
      history.push(
        `/${withdrawal}/${examRegistrations.route}/${data.uuid}/${nonNationalReason.route}`
      ),
    onError: (err) => {
      setErrorMessage(err?.response?.data.message!);
    },
  });

  if (isLoadingWithdrawalInstructionData) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      {withdrawalInstructionData && (
        <>
          <TitleHeader title={t('withdrawal->>WithdrawFromExam.ExamWithdrawal')} />
          <ContentTemplate type="full">
            <SectionHeader title="Important" />
            <Typography className={classes.headerStatement} variant="h3">
              {t('withdrawal->>WithdrawFromExam.HeaderStatement')}
            </Typography>
            <Box>
              <Typography className={classes.paragraph} variant="body1">{`1. ${t(
                'withdrawal->>WithdrawFromExam.NonNationalContent1'
              )}`}</Typography>
              <Typography variant="body1">{`2. ${t(
                'withdrawal->>WithdrawFromExam.NonNationalContent2'
              )}`}</Typography>
            </Box>
            <Divider paddingTop />
            <SectionHeader title={t('withdrawal->>WithdrawFromExam.GetStarted')} />
            <Typography className={classes.headerStatement} variant="body1">
              {t(
                'withdrawal->>WithdrawFromExam.You are about to withdraw from the following exam registration.'
              )}
            </Typography>
            <Grid container>
              <GridKeyValue
                label="Application ID"
                value={withdrawalInstructionData.applicationId}
              />

              <GridKeyValue
                label="Exam Level(s)"
                value={
                  withdrawalInstructionData.examLevel.toString() === 'SAEIS'
                    ? 'S-AEIS'
                    : withdrawalInstructionData.examLevel.toString()
                }
              />
            </Grid>
            {errorMessage && (
              <Typography className={classes.errorMessage} variant="body2" color="error">
                {errorMessage}
              </Typography>
            )}
            <Button
              id="button-resitReceipt-goToRegisteredExams"
              className={classes.button}
              onClick={() => {
                transactionStart();
                mutatePostCreateExamWithdrawalDraft({
                  uuid: withdrawalInstructionData.uuid,
                });
              }}
              disabled={!withdrawalInstructionData.isWithdrawOpen}
            >
              {t('withdrawal->>WithdrawFromExam.Withdraw from Exam')}
            </Button>
          </ContentTemplate>
        </>
      )}
    </>
  );
};

export default WithdrawFromExam;
